/**
 * useDemoEmailRouting — Manages demo email routing configuration for trial orgs.
 * When enabled, all outbound emails are rerouted to the trial admin's inbox.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "@/contexts/OrgContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface DemoEmailRouting {
  id: string;
  org_id: string;
  enabled: boolean;
  redirect_email: string;
  enabled_by: string | null;
  batch_mode: boolean;
  created_at: string;
  updated_at: string;
}

export function useDemoEmailRouting() {
  const { orgId } = useOrg();

  return useQuery({
    queryKey: ["demo-email-routing", orgId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("demo_email_routing")
        .select("*")
        .eq("org_id", orgId)
        .maybeSingle();
      if (error) throw error;
      return data as DemoEmailRouting | null;
    },
    enabled: !!orgId,
  });
}

export function useToggleDemoEmailRouting() {
  const { orgId } = useOrg();
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      enabled,
      redirectEmail,
      batchMode,
    }: {
      enabled: boolean;
      redirectEmail?: string;
      batchMode?: boolean;
    }) => {
      if (!orgId) throw new Error("No org context");

      const { data: existing } = await (supabase as any)
        .from("demo_email_routing")
        .select("id")
        .eq("org_id", orgId)
        .maybeSingle();

      if (existing) {
        const updates: Record<string, unknown> = {
          enabled,
          updated_at: new Date().toISOString(),
        };
        if (redirectEmail !== undefined) updates.redirect_email = redirectEmail;
        if (batchMode !== undefined) updates.batch_mode = batchMode;
        if (enabled) updates.enabled_by = user?.id || null;

        const { error } = await (supabase as any).from("demo_email_routing").update(updates).eq("org_id", orgId);
        if (error) throw error;
      } else {
        if (!redirectEmail) throw new Error("Redirect email is required to enable demo routing");
        const { error } = await (supabase as any).from("demo_email_routing").insert({
          org_id: orgId,
          enabled,
          redirect_email: redirectEmail,
          enabled_by: user?.id || null,
          batch_mode: batchMode ?? true,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["demo-email-routing", orgId] });
      toast.success("Demo email routing updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useFlushDemoBatch() {
  const { orgId } = useOrg();

  return useMutation({
    mutationFn: async (batchKey?: string) => {
      const { data, error } = await supabase.functions.invoke("flush-demo-batch", {
        body: {
          orgId,
          ...(batchKey ? { batchKey } : {}),
        },
      });
      if (error) throw error;
      return data as { success: boolean; flushed: number; batches: number };
    },
    onSuccess: (data) => {
      if (data.flushed > 0) {
        toast.success(
          `Flushed ${data.flushed} email${data.flushed === 1 ? "" : "s"} into ${data.batches} digest${data.batches === 1 ? "" : "s"}`,
        );
      } else {
        toast.info("No pending batch emails to flush");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDemoBatchItems() {
  const { orgId } = useOrg();

  return useQuery({
    queryKey: ["demo-batch-items", orgId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("demo_email_batch_items")
        .select("*")
        .eq("org_id", orgId)
        .is("flushed_at", null)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as Array<{
        id: string;
        batch_key: string;
        template_name: string;
        recipient_email: string;
        recipient_name: string | null;
        subject: string;
        created_at: string;
      }>;
    },
    enabled: !!orgId,
    refetchInterval: 10000,
  });
}
